import type { ProjectData, ProjectListItem, ServiceType } from "./types";
import { projectImages } from "./imageRegistry";

export type { ProjectData, ProjectListItem, ServiceType };

export const slugify = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/**
 * Full internal record set. Never rendered directly — public surfaces must use
 * `allProjectsList`, which excludes projects whose codelumen role is still
 * under audit. Records are retained here (not deleted) for that audit.
 */
export const allProjectRecords: ProjectListItem[] = [
  // ── Featured globe (homepage), one per market ────────────────────────────
  { name: "Media Monks", type: "Office Space", location: "Madrid, Spain", serviceType: "Lighting Design", client: "Media.Monks", country: "Spain", featured: true },
  { name: "Google Offices", type: "Office Space", location: "New York City, NY", serviceType: "Lighting Design", client: "Google", country: "USA", featured: true, needsRealImage: true, inProgress: true },
  { name: "Ritz Carlton", type: "Hospitality", location: "San Jose del Cabo, MX", serviceType: "Value Analysis", client: "Marriott Luxury Collection", country: "Mexico", featured: true, needsRealImage: true },
  { name: "Marriott", type: "Multifamily", location: "Dallas, TX", serviceType: "Value Analysis", client: "Marriott International", country: "USA", needsRealImage: true },
  { name: "Birkenstock", type: "Office Space", location: "Mexico City", serviceType: "Lighting Design", client: "Birkenstock", country: "Mexico", featured: true },
  { name: "El Paso Star", type: "Public Works", location: "El Paso, TX", serviceType: "Lighting Design", roleLabel: "Lighting Design + Execution", client: "City of El Paso", country: "USA", featured: true },
  { name: "Lear Corporation", type: "Industrial", location: "Cd. Juárez, MX", serviceType: "Value Analysis", client: "Lear Corporation", country: "Mexico", featured: true, needsRealImage: true },
  { name: "Grupo Bafar", type: "Office Space", location: "Chihuahua, México", serviceType: "Lighting Design", client: "Grupo Bafar", country: "Mexico", featured: true },
  { name: "The Creamery", type: "Adaptive Reuse", location: "San Antonio, TX", serviceType: "Lighting Design", client: "Private Developer", country: "USA", featured: true },
  { name: "Lund Residence", type: "Upscale Residential", location: "Austin, TX", serviceType: "Lighting Design", country: "USA", needsRealImage: true, inProgress: true },
  { name: "Effy Jewelry", type: "Retail", location: "Nassau, Bahamas", serviceType: "Lighting Design", client: "Effy Jewelry", country: "Bahamas", featured: true },
  { name: "Uno South Pointe", type: "Office Space", location: "Miami South Beach", serviceType: "Lighting Design", client: "Uno South Pointe", country: "USA", featured: true },

  // ── Active Projects (in execution) ───────────────────────────────────────
  { name: "Rim Residence", type: "Upscale Residential", location: "El Paso, TX", serviceType: "Partial Lighting Design", country: "USA", inProgress: true, releaseLabel: "Coming in Winter 2026" },
  { name: "Tango Room", type: "Hospitality", location: "Frisco, TX", serviceType: "Lighting Design", country: "USA", needsRealImage: true, inProgress: true, releaseLabel: "Coming in Fall 2026" },
  { name: "Flo Networks", type: "Office Space", location: "Soho, New York City, NY", serviceType: "Lighting Design", client: "Flo Networks", country: "USA", needsRealImage: true, inProgress: true, releaseLabel: "Coming in Fall 2026" },
  { name: "Commissary", type: "Hospitality", location: "Frisco, TX", serviceType: "Lighting Design", country: "USA", needsRealImage: true, inProgress: true, releaseLabel: "Coming in Fall 2026" },
  { name: "Morga Residence", type: "Upscale Residential", location: "El Paso, TX", serviceType: "Lighting Design", country: "USA", needsRealImage: true, inProgress: true },
  { name: "Pueblo Viejo Interactive Park", type: "Public Works", location: "El Paso, TX", serviceType: "Lighting Design", country: "USA", needsRealImage: true, inProgress: true, releaseLabel: "Coming in Fall 2026" },
  { name: "Marty Robbins Interactive Park", type: "Public Works", location: "El Paso, TX", serviceType: "Lighting Design", country: "USA", needsRealImage: true, inProgress: true, releaseLabel: "Coming in Winter 2027" },
  { name: "Pony Creek Ranch", type: "Upscale Residential", location: "Dallas, TX", serviceType: "Lighting Design", country: "USA", needsRealImage: true, inProgress: true, releaseLabel: "Coming in Winter 2027" },
  { name: "Camil Residence", type: "Upscale Residential", location: "Las Cruces, NM", serviceType: "Lighting Design", country: "USA", needsRealImage: true, inProgress: true, releaseLabel: "Coming in Winter 2027" },
  // Temporarily hidden — to be re-added later:
  // Vin Fraga at Montecillo Apartments, Montecillo 19, EPT Land Communities, Evaga Restaurant
  { name: "Shy Boy", type: "Hospitality", location: "Downtown Dallas, TX", serviceType: "Lighting Design", client: "Private Hospitality Group", country: "USA", featured: true },

  // ── Library only (non-featured) ──────────────────────────────────────────
  { name: "Casa MS", type: "Upscale Residential", location: "Chihuahua, México", serviceType: "Lighting Design", client: "Private", country: "Mexico" },
  { name: "Private Residence", type: "Upscale Residential", location: "Private Residence, Mexico", serviceType: "Lighting Design", client: "Undisclosed", country: "Mexico" },
  { name: "Rappi", type: "Office Space", location: "Mexico City", serviceType: "Lighting Design", client: "Rappi", country: "Mexico" },
  { name: "Tekmovil", type: "Office Space", location: "Mexico City", serviceType: "Lighting Design", client: "Tekmovil", country: "Mexico" },
  { name: "Telemundo", type: "Office Space", location: "El Paso, TX", serviceType: "Lighting Design", client: "Telemundo (NBCUniversal)", country: "USA" },
  { name: "Speak Easy", type: "Hospitality", location: "El Paso, TX", serviceType: "Lighting Design", country: "USA" },
  { name: "Private Penthouse", type: "Upscale Residential", location: "New York City, NY", serviceType: "Lighting Design", country: "USA" },
  { name: "Desert Commons", type: "Multifamily", location: "El Paso, TX", serviceType: "Value Analysis", country: "USA" },
  { name: "Rothenbucher", type: "Retail", location: "Cd. Juárez, MX", serviceType: "Lighting Design", country: "Mexico" },
  { name: "San Antonio St.", type: "Urban", location: "El Paso, TX", serviceType: "Lighting Design", country: "USA" },
  { name: "St. Rogers", type: "Hospitality", location: "El Paso, TX", serviceType: "Lighting Design", country: "USA" },
  { name: "Electricity Sign", type: "Public Works", location: "El Paso, TX", serviceType: "Lighting Design", country: "USA" },
  { name: "Campbell Apartments", type: "Multifamily", location: "El Paso, TX", serviceType: "Value Analysis", country: "USA" },
  { name: "Quill", type: "Hospitality", location: "Dallas, TX", serviceType: "Value Analysis", country: "USA" },
  { name: "Desert Bloom", type: "Medical", location: "El Paso, TX", serviceType: "Value Analysis", country: "USA" },
  { name: "Sessions", type: "Sports", location: "El Paso, TX", serviceType: "Value Analysis", country: "USA" },
  { name: "Salt & Honey", type: "Hospitality", location: "El Paso, TX", serviceType: "Value Analysis", country: "USA" },
  { name: "Stellar", type: "Hospitality", location: "Dallas, TX", serviceType: "Value Analysis", country: "USA" },
  { name: "Davenport", type: "Hospitality", location: "El Paso, TX", serviceType: "Value Analysis", country: "USA" },
  { name: "Westview Residence", type: "Upscale Residential", location: "State College, PA", serviceType: "Value Analysis", country: "USA" },
  { name: "Headquarters", type: "Hospitality", location: "El Paso, TX", serviceType: "Value Analysis", country: "USA" },
  { name: "Weekend Gallery", type: "Retail", location: "El Paso, TX", serviceType: "Lighting Design", country: "USA" },
  { name: "Even Coast", type: "Hospitality", location: "Dallas, TX", serviceType: "Lighting Design", country: "USA", featured: true },
];

/**
 * Public visibility gate. Projects whose codelumen role was limited to a
 * value-analysis scope are withheld from all public rendering pending a role
 * audit. Their records and assets stay in place.
 */
export const isPublicProject = (p: ProjectListItem): boolean =>
  p.serviceType !== "Value Analysis";

/** Every project that may be rendered publicly, in editorial source order. */
export const allProjectsList: ProjectListItem[] = allProjectRecords.filter(isPublicProject);

/** Slugs withheld from public rendering (project pages, lists, recommendations). */
export const withheldProjectSlugs: ReadonlySet<string> = new Set(
  allProjectRecords.filter((p) => !isPublicProject(p)).map((p) => slugify(p.name)),
);

export const isWithheldProjectSlug = (slug?: string): boolean =>
  !!slug && withheldProjectSlugs.has(slug);

// Dynamic project data loader, each project is its own chunk
const projectModules: Record<string, () => Promise<{ default: ProjectData }>> = {
  "the-creamery": () => import("./the-creamery"),
  "even-coast": () => import("./even-coast"),
  "el-paso-star": () => import("./el-paso-star"),
  "casa-ms": () => import("./casa-ms"),
  "private-residence": () => import("./private-residence"),
  "rappi": () => import("./rappi"),
  "media-monks": () => import("./media-monks"),
  "tekmovil": () => import("./tekmovil"),
  "telemundo": () => import("./telemundo"),
  "ritz-carlton": () => import("./ritz-carlton"),
  "speak-easy": () => import("./speak-easy"),
  "marriott": () => import("./marriott"),
  "birkenstock": () => import("./birkenstock"),
  "private-penthouse": () => import("./private-penthouse"),
  "desert-commons": () => import("./desert-commons"),
  "rothenbucher": () => import("./rothenbucher"),
  "san-antonio-st": () => import("./san-antonio-st"),
  "st-rogers": () => import("./st-rogers"),
  "electricity-sign": () => import("./electricity-sign"),
  "campbell-apartments": () => import("./campbell-apartments"),
  "quill": () => import("./quill"),
  "desert-bloom": () => import("./desert-bloom"),
  "sessions": () => import("./sessions"),
  "salt---honey": () => import("./salt-honey"),
  "stellar": () => import("./stellar"),
  "davenport": () => import("./davenport"),
  "lear-corporation": () => import("./lear-corporation"),
  "westview-residence": () => import("./westview-residence"),
  "headquarters": () => import("./headquarters"),
  "weekend-gallery": () => import("./weekend-gallery"),
  "google-offices": () => import("./google"),
  
  "effy-jewelry": () => import("./effy-jewelry"),
  "grupo-bafar": () => import("./bafar"),
  "bafar-corporate-offices": () => import("./bafar"),
  "uno-south-miami": () => import("./uno-south-miami"),
  "uno-south-pointe": () => import("./uno-south-miami"),
  "pueblo-viejo-interactive-park": () => import("./pueblo-viejo-interactive-park"),
  "shy-boy": () => import("./shy-boy"),
  "tango-room": () => import("./tango-room"),
  "commissary": () => import("./commissary"),
  "flo-networks": () => import("./flo-networks"),
  "lund-residence": () => import("./lund-residence"),
  "rim-residence": () => import("./rim-residence"),
  "camil-residence": () => import("./camil-residence"),
  "morga-residence": () => import("./morga-residence"),
  "pony-creek-ranch": () => import("./pony-creek-ranch"),
  "marty-robbins-interactive-park": () => import("./marty-robbins-interactive-park"),
};

export async function loadProjectData(slug: string): Promise<ProjectData | null> {
  const loader = projectModules[slug];
  if (!loader) return null;
  try {
    const mod = await loader();
    return mod.default;
  } catch {
    return null;
  }
}

/**
 * Minimal record used when a listed project has no dedicated page module yet.
 * Deliberately carries NO quantitative impact metrics: numbers are only ever
 * rendered from project-specific evidence held in that project's own file.
 */
export const createDefaultProject = (name: string, type: string, location: string, serviceType: ServiceType = "In Progress"): ProjectData => {
  const registryImage = projectImages[name];
  return ({
  title: name,
  location,
  category: type,
  serviceType,
  // Neutral identification only. No service, product, controls, platform,
  // performance, or outcome claim may appear without project-specific evidence.
  heroMedia: registryImage ?? "",
  challenge: "Project documentation is being prepared.",
  impactStats: [],
  ecosystem: [],
  gallery: [],
  products: [],
});
};
